import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormGroup } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { finalize } from 'rxjs/operators';

import { ToasterService } from '../../../../../../core/services/toaster.service';
import { DateUtil } from '../../../../../../core/utils/date-util';
import { LocalStorageUtil } from '../../../../../../core/utils/local-storage-util';
import { FileViewerComponent } from '../../../../../shared/components/form-components/components/file-viewer/file-viewer.component';
import { ModalBaseClass } from '../../../../../shared/components/modals/base_class/modal-base-class';
import { NotificationComponent } from '../../../../../shared/components/modals/notification/notification.component';
import { ModalButtonType } from '../../../../../../models/common/modal/modal-config';
import { AdvanceTaskService } from '../../../../../../state/modules/advance-task/services/advance-task.service';
import { AdvanceTaskCommentDto } from '../../../../../../state/modules/advance-task/types/advance-task.types';
import { ResourceApiError } from '../../../../../../state/modules/resource/resource-response.util';

import {
  COMMENT_FIELD,
  COMMENT_IMAGE_MAX_BYTES,
  COMMENT_IMAGE_TOO_BIG,
  COMMENT_NOT_AN_IMAGE,
  DELETE_COMMENT_MODAL_CONFIG,
  NO_ACCESS_OTHERS_COMMENTS,
  NO_COMMENTS_NO_RECORD,
} from '../advance-task-activity.constant';

/**
 * One task's comments for the shift's own day (guide §4.3). Any assignee can post; the API lets a
 * comment carry text, one image, or both, and only its author may delete it.
 */
@Component({
  standalone: false,
  selector: 'kt-advance-task-comments',
  templateUrl: './task-comments.component.html',
  styleUrls: ['./task-comments.component.scss'],
})
export class TaskCommentsComponent extends ModalBaseClass implements OnInit {

  /** Set by the caller. */
  taskId: number = null;
  taskName = '';
  /** due_date from the feed: the day these comments belong to. */
  dueDate: string = null;
  /** The shift's location; the upload API files the image under it. */
  locationId: number = null;
  /** Whose comments to load: 0 = the logged-in user's own; another id when viewing that person's feed. */
  userId = 0;
  readOnly = false;
  /** Why commenting is off, so the missing box is explained rather than just absent. */
  readOnlyReason = '';
  /** Told the comment count after loading, posting and deleting, so the task row's number stays right. */
  onCountChange: (count: number) => void = () => { };

  noComments = NO_COMMENTS_NO_RECORD;
  noAccessRecord = NO_ACCESS_OTHERS_COMMENTS;
  /** Another employee's comments refused (204): only a System Admin or Location Manager may see them. */
  noAccess = signal(false);

  /** kt-textarea adds the comment control here; the control holds the text. */
  form = new UntypedFormGroup({});
  commentField = COMMENT_FIELD;
  private formValue = toSignal(this.form.valueChanges, { initialValue: {} });
  text = computed(() => `${this.formValue()[COMMENT_FIELD.key] ?? ''}`);

  loading = signal(false);
  saving = signal(false);
  comments = signal<AdvanceTaskCommentDto[]>([]);
  imageUrl = signal<string | null>(null);
  imageName = signal('');
  uploading = signal(false);

  private advanceTask = inject(AdvanceTaskService);
  private toaster = inject(ToasterService);
  private loggedInUserId = LocalStorageUtil.getUserId();

  canPost = computed(() => !!this.text().trim() || !!this.imageUrl());

  constructor(public activeModal: NgbActiveModal, public modalService: NgbModal) {
    super(modalService);
  }

  ngOnInit() {
    this.loadComments();
  }

  private loadComments() {
    this.loading.set(true);
    this.noAccess.set(false);
    this.advanceTask.getTaskComments(this.taskId, this.dueDate, this.userId).subscribe({
      next: comments => {
        this.loading.set(false);
        this.comments.set(comments);
        this.onCountChange(comments.length);
      },
      error: (error: ResourceApiError) => {
        this.loading.set(false);
        // 204 on someone else's comments = not allowed (not an error): say so in the pop-up instead of a toast.
        if (error.status === 204 && this.userId) {
          this.noAccess.set(true);
          return;
        }
        this.toaster.errorShow(error.message);
      },
    });
  }

  isMine(comment: AdvanceTaskCommentDto): boolean {
    return comment.advance_task_comment_fk_user_id === this.loggedInUserId;
  }

  authorLabel(comment: AdvanceTaskCommentDto): string {
    return this.isMine(comment) ? 'You' : comment.author_name || '';
  }

  timeLabel(comment: AdvanceTaskCommentDto): string {
    return DateUtil.utcToBusinessDateTime(comment.advance_task_comment_created_at);
  }

  /** One image per comment, so a new pick replaces the one already attached. */
  fileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    input.value = '';
    if (!file) {
      return;
    }
    if (file.type.indexOf('image/') !== 0) {
      this.toaster.errorShow(COMMENT_NOT_AN_IMAGE);

      return;
    }
    if (file.size > COMMENT_IMAGE_MAX_BYTES) {
      this.toaster.errorShow(COMMENT_IMAGE_TOO_BIG);

      return;
    }

    this.uploading.set(true);
    this.advanceTask.uploadCommentImage(file, this.locationId).pipe(
      finalize(() => this.uploading.set(false)),
    ).subscribe({
      next: result => {
        this.imageUrl.set(result.url);
        this.imageName.set(file.name);
      },
      error: (error: ResourceApiError) => this.toaster.errorShow(error.message),
    });
  }

  removeImage() {
    this.imageUrl.set(null);
    this.imageName.set('');
  }

  /** The app's file viewer pop-up, so the photo's address isn't opened in a new tab. */
  openPhoto(url: string) {
    // Comment photos are always images; the viewer would otherwise guess from the file extension.
    this.openPopUp(FileViewerComponent, { fileUrl: url, isImage: true },
      { centered: true, size: 'xl', keyboard: true, backdrop: 'static' });
  }

  post() {
    if (this.form.invalid) {
      // kt-textarea shows the "too long" error below the box once touched.
      this.form.markAllAsTouched();
      return;
    }
    if (!this.canPost() || this.saving() || this.uploading()) {
      return;
    }
    this.saving.set(true);
    this.advanceTask.addTaskComment({
      advance_task_comment_fk_advance_task_id: this.taskId,
      advance_task_comment_date: this.dueDate,
      advance_task_comment_text: this.text().trim() || null,
      advance_task_comment_scan_image_url: this.imageUrl(),
    }).pipe(
      finalize(() => this.saving.set(false)),
    ).subscribe({
      next: comment => {
        this.comments.update(list => [...list, comment]);
        this.onCountChange(this.comments().length);
        this.form.reset();
        this.removeImage();
      },
      error: (error: ResourceApiError) => this.toaster.errorShow(error.message),
    });
  }

  deleteComment(comment: AdvanceTaskCommentDto) {
    const modalRef = this.openPopUp(NotificationComponent, {
      options: DELETE_COMMENT_MODAL_CONFIG(),
    }, { centered: true, backdrop: 'static' });

    modalRef.result.then(result => {
      if (!result || result.STATUS !== ModalButtonType.DELETE) {
        return;
      }
      this.removeComment(comment);
    }, () => { });
  }

  private removeComment(comment: AdvanceTaskCommentDto) {
    this.loading.set(true);
    this.advanceTask.deleteTaskComment(comment.advance_task_comment_id).subscribe({
      next: () => {
        this.loading.set(false);
        this.comments.update(list => list
          .filter(row => row.advance_task_comment_id !== comment.advance_task_comment_id));
        this.onCountChange(this.comments().length);
      },
      error: (error: ResourceApiError) => {
        this.loading.set(false);
        this.toaster.errorShow(error.message);
      },
    });
  }
}
